import { CURRENT_VERSION, type ProjectFile, ProjectFileSchema, type SceneDoc, SceneSchema } from './schema'

/** Each entry upgrades a scene from version `n` to `n + 1`. Add one whenever the schema changes shape. */
export type RawScene = Record<string, unknown>
export type Migration = (scene: RawScene) => RawScene
export const MIGRATIONS: Record<number, Migration> = {
  // 1 → 2: sources gained a reference radius (size); older projects behave as point sources.
  1: (scene) => ({ ...scene, sources: ((scene.sources as object[] | undefined) ?? []).map((s) => ({ size: 1, ...s })) }),
  // 2 → 3: round and ring rooms, hollow tubes. Existing rooms are boxes; existing primitives have no tube settings.
  2: (scene) => {
    const enclosure = (scene.enclosure ?? {}) as { materials?: Record<string, string> }
    const mats = enclosure.materials ?? {}
    return {
      ...scene,
      enclosure: { shape: 'box', core: 0, ...enclosure, materials: { wall: mats.west ?? 'concrete', core: 'concrete', ...mats } },
      objects: ((scene.objects as object[] | undefined) ?? []).map((o) => ({ wall: 1, caps: 'none', ...o })),
    }
  },
  // 3 → 4: custom outlines for rooms and extruded shapes. Existing rooms and primitives keep their shapes.
  3: (scene) => ({
    ...scene,
    enclosure: { outline: [], ...((scene.enclosure ?? {}) as object) },
    objects: ((scene.objects as object[] | undefined) ?? []).map((o) => ({ outline: [], holes: [], ...o })),
  }),
}

export class ProjectError extends Error {}

export function migrateScene(
  scene: unknown,
  fromVersion: number,
  migrations: Record<number, Migration> = MIGRATIONS,
  target = CURRENT_VERSION,
): unknown {
  if (fromVersion > target) {
    throw new ProjectError(`Project was saved by a newer version (format ${fromVersion}, this app reads ${target}).`)
  }
  let out = scene
  for (let v = fromVersion; v < target; v++) {
    const step = migrations[v]
    if (!step) throw new ProjectError(`No migration from project format ${v} to ${v + 1}.`)
    out = step(out as RawScene)
  }
  return out
}

/** Validates the envelope, migrates the scene to the current version and validates the result. */
export function parseProject(raw: unknown): { project: ProjectFile; scene: SceneDoc } {
  const env = ProjectFileSchema.safeParse(raw)
  if (!env.success) throw new ProjectError('Not a MEGASPACE project file.')
  const migrated = migrateScene(env.data.scene, env.data.version)
  const scene = SceneSchema.safeParse(migrated)
  if (!scene.success) {
    const first = scene.error.issues[0]
    throw new ProjectError(`Invalid scene data at "${first?.path.join('.') ?? ''}": ${first?.message ?? 'unknown error'}`)
  }
  return { project: env.data, scene: scene.data }
}
